import { useEffect, type ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { DATA_AUTHORITY, FOUNDER_SLOTS, LEGAL_UPDATED, PROVIDER, SUBSCRIPTION_TIERS, formatEuro } from "@handvero/shared";
import { Page, PageHead } from "../components/ui";

const ADDRESS = `${PROVIDER.street}, ${PROVIDER.zip} ${PROVIDER.city}`;

function Part({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section className="stack legal-part" id={id}>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function H({ children }: { children: ReactNode }) {
  return <h3>{children}</h3>;
}

function Impressum() {
  return (
    <Part id="impressum" title="Impressum">
      <H>Angaben nach § 5 Digitale-Dienste-Gesetz (DDG)</H>
      <p>
        {PROVIDER.brand}
        <br />
        {PROVIDER.owner} ({PROVIDER.legalForm})
        <br />
        {PROVIDER.street}
        <br />
        {PROVIDER.zip} {PROVIDER.city}
      </p>
      <H>Kontakt</H>
      <p>
        Telefon: <a href={`tel:${PROVIDER.phoneLink}`}>{PROVIDER.phone}</a>
        <br />
        E-Mail: <a href={`mailto:${PROVIDER.email}`}>{PROVIDER.email}</a>
      </p>
      {(PROVIDER.vatId || PROVIDER.smallBusiness || PROVIDER.register) && (
        <>
          <H>Steuern und Register</H>
          {PROVIDER.vatId && <p>Umsatzsteuer-Identifikationsnummer nach § 27a Umsatzsteuergesetz: {PROVIDER.vatId}</p>}
          {PROVIDER.smallBusiness && !PROVIDER.vatId && <p>Kleinunternehmer im Sinne von § 19 Umsatzsteuergesetz. Es wird keine Umsatzsteuer ausgewiesen.</p>}
          {PROVIDER.register && <p>Registereintrag: {PROVIDER.register}</p>}
        </>
      )}
      <H>Verantwortlich für den Inhalt nach § 18 Abs. 2 Medienstaatsvertrag</H>
      <p>
        {PROVIDER.owner}, {ADDRESS}
      </p>
      <H>Streitbeilegung</H>
      <p>
        Wir sind weder verpflichtet noch bereit, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen. Das
        Angebot für Betriebe richtet sich ausschließlich an Unternehmer. Für Privatpersonen gelten die <Link to="/privat/agb">Bedingungen von HandVero Zuhause</Link>.
      </p>
      <H>Haftung für Inhalte und Links</H>
      <p>
        Wir bemühen uns, die Inhalte dieser Website richtig und aktuell zu halten, übernehmen dafür aber keine Gewähr. Für Inhalte externer Seiten, auf
        die wir verlinken, sind allein deren Betreiber verantwortlich. Sollte uns eine Rechtsverletzung bekannt werden, entfernen wir den Link
        unverzüglich.
      </p>
      <H>Urheberrecht</H>
      <p>
        Texte, Grafiken und die Software von HandVero sind urheberrechtlich geschützt. Eine Verwendung außerhalb der Nutzung von HandVero bedarf unserer
        schriftlichen Zustimmung.
      </p>
    </Part>
  );
}

function Datenschutz() {
  return (
    <Part id="datenschutz" title="Datenschutzerklärung">
      <p className="lead">
        Hier erklären wir verständlich, welche Daten wir verarbeiten, wenn Sie diese Website besuchen oder HandVero nutzen, wofür wir sie brauchen und
        welche Rechte Sie haben.
      </p>

      <H>1. Verantwortlicher</H>
      <p>
        {PROVIDER.owner}, {PROVIDER.brand}, {ADDRESS}
        <br />
        E-Mail <a href={`mailto:${PROVIDER.email}`}>{PROVIDER.email}</a>
      </p>
      <p>
        Für die Daten, die Sie selbst in HandVero ablegen (zum Beispiel Fotos, Dokumente und Kundendaten Ihrer Aufträge), sind Sie verantwortlich. Wir
        verarbeiten sie in Ihrem Auftrag, siehe Punkt 7 und{" "}
        <a href="#avv">Vereinbarung zur Auftragsverarbeitung</a>.
      </p>

      <H>2. Das Wichtigste vorab</H>
      <ul className="checks">
        <li>Wir setzen kein Tracking und keine Werbe- oder Analysedienste ein.</li>
        <li>Die Website lädt keine Schriften, Skripte oder Bilder von fremden Servern.</li>
        <li>Wir verwenden nur Cookies und Speicher, die für den Betrieb nötig sind. Deshalb ist keine Einwilligung nötig. Beim ersten Besuch weisen wir Sie trotzdem darauf hin; den Hinweis können Sie über „Cookie-Hinweis“ in der Fußzeile jederzeit wieder öffnen.</li>
        <li>Unsere Server stehen in Deutschland.</li>
      </ul>

      <H>3. Besuch der Website und Server-Protokolle</H>
      <p>
        Beim Aufruf verarbeitet unser Server technisch notwendige Daten: IP-Adresse, Datum und Uhrzeit, aufgerufene Adresse (ohne Suchbegriffe und ohne
        geheime Link-Kennungen) und Antwortcode. Wir speichern sie in Protokollen, damit wir Fehler finden und Angriffe abwehren können. Die Protokolle
        werden automatisch überschrieben, sobald sie eine feste Größe erreichen. Je nach Nutzung dauert das einige Tage bis wenige Wochen.
      </p>
      <p>Rechtsgrundlage ist Art. 6 Abs. 1 Buchstabe f DSGVO (unser berechtigtes Interesse an einem sicheren und stabilen Betrieb).</p>

      <H>4. Hosting</H>
      <p>
        HandVero läuft auf Servern der Hetzner Online GmbH, Industriestr. 25, 91710 Gunzenhausen, in einem Rechenzentrum in Deutschland. Mit Hetzner
        besteht ein Vertrag zur Auftragsverarbeitung nach Art. 28 DSGVO. Hetzner verarbeitet Daten nur nach unserer Weisung. Rechtsgrundlage ist Art. 6 Abs. 1 Buchstabe b und f DSGVO (Bereitstellung von HandVero), für Ihre Inhalte
        Art. 28 DSGVO.
      </p>

      <H>5. Cookies und Speicher im Browser</H>
      <p>Wir verwenden ausschließlich technisch notwendige Cookies und Browser-Speicher. Rechtsgrundlage ist § 25 Abs. 2 Nr. 2 TDDDG und Art. 6 Abs. 1 Buchstabe b und f DSGVO.</p>
      <ul className="checks">
        <li>
          <b>Anmelde-Cookie (hv_session):</b> Hält Sie angemeldet. Es enthält nur eine zufällige Kennung und gilt bis zu 30 Tage. Mit dem Abmelden wird es
          ungültig.
        </li>
        <li>
          <b>Kurzlebiges Cookie:</b> Nur wenn Sie Ihren Outlook-Kalender verbinden, setzen wir für wenige Minuten ein weiteres Cookie, das die Anmeldung bei
          Microsoft absichert.
        </li>
        <li>
          <b>Speicher im Browser (Local Storage):</b> Er merkt sich Einstellungen auf Ihrem Gerät, zum Beispiel Darstellung, den zuletzt geöffneten
          Ordner, ob die Sprachausgabe an ist oder dass Sie den Cookie-Hinweis gesehen haben (Eintrag „hv.cookie-hinweis“ mit dem Zeitpunkt). Der Sitzungsspeicher (Session Storage) merkt sich, ob die App in dieser Sitzung schon gestartet wurde. Diese Angaben verlassen Ihr Gerät nicht.
        </li>
      </ul>

      <H>6. Registrierung und Kundenkonto</H>
      <p>
        Für ein Firmenkonto speichern wir: Firmenname, Gewerk, Name und geschäftliche E-Mail-Adresse der Ansprechperson, Telefonnummer, Ihr Passwort
        (nur als Hash, nicht umkehrbar), die freiwillige Angabe zur Mitarbeiterzahl und die Zeitpunkte von Registrierung und Anmeldung.
        Laden Sie Mitarbeiter ein, speichern wir deren Namen und E-Mail-Adressen. Für jede Anmeldung merken wir uns den Gerätetyp (User-Agent), damit Sie Ihre
        angemeldeten Geräte sehen und beenden können, und die IP-Adresse, um Missbrauch zu erkennen.
      </p>
      <p>
        Für ein Privatkonto („HandVero Zuhause“) speichern wir nur Ihren Namen, Ihre E-Mail-Adresse, Ihr Passwort (als Hash), Ihre Zustimmungen (Nutzungsbedingungen, Hinweis zur Werbung, freiwillige Tipps-Mails) mit Zeitpunkt sowie die Inhalte, die Sie selbst ablegen. Mehr dazu in den{" "}
        <Link to="/privat/agb#datenschutz">Datenschutzhinweisen für Privatpersonen</Link>.
      </p>
      <p>
        Rechtsgrundlage ist Art. 6 Abs. 1 Buchstabe b DSGVO (Vertrag). Für die Daten eingeladener Mitarbeiter ist es Art. 6 Abs. 1 Buchstabe f DSGVO (Bereitstellung des
        Firmenkontos). Wir löschen die Daten, wenn Sie Ihr Konto oder Ihre Firma löschen, siehe Punkt 13.
      </p>
      <p>
        <b>Prüfung der Registrierung.</b> Um Missbrauch zu verhindern, prüfen wir jede Registrierung automatisch. Wir speichern dazu IP-Adresse,
        E-Mail-Adresse, deren Domain und das Ergebnis der Prüfung. Wir schauen, ob die Domain einen Mailserver hat (dafür fragen wir das öffentliche
        Domain-Namensystem ab), ob es sich um eine Wegwerf-Adresse handelt, ob das Formular wie von einem Programm ausgefüllt wurde und ob von derselben
        IP-Adresse oder Firmen-Domain in kurzer Zeit auffällig viele Registrierungen kommen. Eine Registrierung kann dadurch automatisch abgelehnt
        werden. Wir nennen Ihnen dann den Grund. Die automatische Prüfung ist für den Abschluss des Vertrags erforderlich und dient dem Schutz vor Missbrauch
        (Art. 22 Abs. 2 Buchstabe a und Art. 6 Abs. 1 Buchstabe f DSGVO). Sie können verlangen, dass ein Mensch entscheidet, Ihren Standpunkt darlegen
        und die Entscheidung anfechten: Schreiben Sie uns einfach, dann prüft ein Mensch Ihre Anmeldung. Diese Daten löschen wir nach 90 Tagen. Registrierungen, die nicht per E-Mail bestätigt werden, löschen wir nach 30 Tagen.
      </p>

      <H>7. Nutzung von HandVero und Ihre Inhalte</H>
      <p>
        Alles, was Sie oder Ihre Mitarbeiter in HandVero ablegen, speichern wir für Sie: Projekte und Ordner, Fotos, Dokumente, Notizen, Sprachmemos, Chat-Nachrichten zwischen Kollegen, Termine,
        Protokolle, Unterschriften, Schulungsnachweise und Pläne. Dazu können auch personenbezogene Daten Ihrer Kunden und Mitarbeiter gehören. Wir
        nutzen diese Inhalte ausschließlich, um HandVero für Sie bereitzustellen. Wir werten sie nicht für Werbung aus und geben sie nicht an Dritte
        weiter, außer an die unter Punkt 9 genannten Dienstleister.
      </p>
      <p>
        Voice-Memos nehmen Sie mit dem Mikrofon Ihres Geräts auf (der Browser fragt vorher nach Erlaubnis). Die Aufnahme wird wie Ihre anderen Inhalte gespeichert.
      </p>
      <p>
        Dabei sind Sie Verantwortlicher im Sinne der DSGVO und wir Auftragsverarbeiter. Die Einzelheiten regelt die{" "}
        <a href="#avv">Vereinbarung zur Auftragsverarbeitung</a>, die Teil unserer Nutzungsbedingungen ist. Sie müssen Ihre Kunden und Mitarbeiter selbst
        darüber informieren, dass Sie HandVero einsetzen.
      </p>
      <p>
        <b>Vero, der Assistent.</b> Der Assistent in der App arbeitet auf unseren gemieteten Servern bei Hetzner in Deutschland. Er nutzt keinen externen KI-Dienst, und Ihre Eingaben
        werden nicht an Dritte übermittelt. Die Spracheingabe läuft über das Mikrofon der Tastatur Ihres Handys. Dafür gelten die Datenschutzregeln
        des Herstellers Ihres Geräts.
      </p>
      <p>
        <b>Weitergabe per Link.</b> Wenn Sie ein Protokoll oder einen Termin teilen, erhält die Person einen persönlichen Link. Wer den Link kennt, kann
        die freigegebene Datei oder den Termin ansehen. Protokoll-Links gelten 7 bis 90 Tage und lassen sich vorher widerrufen. Dabei speichern wir die Empfänger-Adresse,
        den letzten Abruf und die Zahl der Downloads. Geteilte Termine bleiben abrufbar, bis Sie den Termin löschen. Beim Kalender-Abo ruft der von Ihnen gewählte Kalenderdienst
        (zum Beispiel Google, Apple oder Microsoft) Ihre Termine regelmäßig über einen geheimen Link ab. Dafür gelten dessen Datenschutzregeln. Sie können den Link jederzeit erneuern oder abschalten.
      </p>
      <p>
        <b>Support.</b> Ihr Konto in HandVero öffnen wir nur mit Ihrer befristeten Freigabe. Sie können sie jederzeit beenden. Festgehalten werden Anfrage, Grund, Freigabe und
        das erste Öffnen. Zur Verwaltung sehen wir ohne Freigabe nur Stammdaten und Kennzahlen (Firmenname, Inhaber, Zahl der Nutzer, Projekte und Dateien, letzte Aktivität). Als Betreiber
        haben wir technisch Zugriff auf die Server und nutzen ihn nur für Betrieb, Fehlerbehebung und Sicherheit.
      </p>
      <p>
        <b>Nutzungsstatistik.</b> Wir speichern, welche Funktionen wann genutzt wurden (zum Beispiel Anmeldung, Datei hochgeladen), nie die Inhalte. So erkennen wir Fehler und Missbrauch und
        sehen, was verbessert werden soll. Rechtsgrundlage ist Art. 6 Abs. 1 Buchstabe f DSGVO. Wir löschen diese Einträge nach 12 Monaten.
      </p>
      <p>Hier handeln wir als Auftragsverarbeiter (Art. 28 DSGVO). Die Rechtsgrundlage für die Verarbeitung bestimmen Sie als Verantwortlicher.</p>

      <H>8. E-Mails von HandVero</H>
      <p>
        Wir verschicken Bestätigungs-, Einladungs-, Passwort- und Termin-Mails sowie, wenn Sie das auslösen, Mails mit Protokollen oder Terminen für Ihre
        Kunden. Der Versand läuft über unser Postfach bei Microsoft 365 (Microsoft Ireland Operations Limited, Dublin, Irland). Dabei verarbeitet
        Microsoft die Empfängeradresse und den Mailinhalt. Wir senden keine Werbung. Wenn Sie in „Mein Konto“ die Wochenübersicht einschalten, bekommen Sie montags früh höchstens eine E-Mail mit Ihren Terminen und überfälligen Rechnungen. Das ist standardmäßig aus, jede Mail enthält einen Link zum Abschalten, und Sie können es jederzeit in „Mein Konto“ ändern. Rechtsgrundlage ist Art. 6 Abs. 1 Buchstabe b DSGVO, bei Mails, die Sie für Ihre Kunden auslösen, Ihr Auftrag nach Art. 28 DSGVO.
      </p>
      <H>Kontaktaufnahme</H>
      <p>
        Wenn Sie uns schreiben, verarbeiten wir Ihre Angaben, um Ihre Anfrage zu beantworten (Art. 6 Abs. 1 Buchstabe b oder f DSGVO). Das gilt auch für das Kontaktformular auf der Website und für Nachrichten, die Sie aus der App über Vero an uns schicken: Name, E-Mail-Adresse, Ihre Nachricht und bei angemeldeten Nutzern die Firma werden auf unserem Server gespeichert und als E-Mail an unser Postfach geschickt. Die Frage an Vero, die Sie dabei mitsenden, wird ebenfalls übermittelt. E-Mails liegen in unserem Postfach bei Microsoft 365. Wir löschen sie,
        wenn sie nicht mehr gebraucht werden, spätestens nach Ablauf gesetzlicher Aufbewahrungsfristen.
      </p>

      <H>9. Empfänger und Dienstleister</H>
      <ul className="checks">
        <li>Hetzner Online GmbH (Hosting, Rechenzentrum in Deutschland).</li>
        <li>
          Microsoft Ireland Operations Limited (Mailversand über Microsoft 365 und, nur wenn Sie die Anbindung selbst einrichten, Ihr Outlook-Kalender).
        </li>
        <li>
          Mollie B.V., Amsterdam, Niederlande (Zahlungsabwicklung, nur wenn Sie ein kostenpflichtiges Abo buchen). Mollie ist für die Zahlungsabwicklung selbst verantwortlich und hat eigene Datenschutzhinweise.
        </li>
      </ul>
      <p>
        <b>Zahlung.</b> Wenn Sie ein Abo buchen, leiten wir Sie zur Bezahlseite von Mollie weiter. Ihre Zahlungsdaten (zum Beispiel Kartennummer oder IBAN) geben Sie nur dort ein, wir sehen und speichern sie nicht.
        An Mollie übermitteln wir Ihren Firmennamen, Ihre E-Mail-Adresse, den Betrag und eine Bestellnummer. Mollie meldet uns zurück, ob die Zahlung eingegangen ist. Wir speichern Betrag, Zeitraum, Zahlungsstatus und die zugehörige
        Rechnungsnummer, um Ihr Abo freizuschalten und Ihnen eine Rechnung zu schicken. Rechtsgrundlage ist Art. 6 Abs. 1 Buchstabe b DSGVO (Vertrag), für die Aufbewahrung der Rechnungen Buchstabe c (handels- und steuerrechtliche Pflichten).
        Mollie sitzt in der Europäischen Union.
      </p>
      <p>
        Weitere Empfänger gibt es nur, wenn Sie selbst Daten weitergeben (Links, Kalender-Abo, Outlook) oder wir gesetzlich verpflichtet sind. Nutzen wir Microsoft, kann nicht ausgeschlossen werden,
        dass Daten in Drittländer (zum Beispiel die USA) gelangen. Grundlage ist der Angemessenheitsbeschluss der EU-Kommission zum EU-US Data Privacy Framework vom 10. Juli 2023 (Art. 45 DSGVO),
        hilfsweise die Standardvertragsklauseln der EU (Art. 46 Abs. 2 Buchstabe c DSGVO). Wenn Sie das nicht möchten, verbinden Sie Ihren Outlook-Kalender nicht. Den Mailversand brauchen wir
        zwingend, zum Beispiel für den Bestätigungslink.
      </p>

      <H>10. Outlook-Kalender (freiwillig)</H>
      <p>
        Wenn Sie Ihren Outlook-Kalender verbinden, melden Sie sich bei Microsoft an und erlauben HandVero, Termine zu lesen und zu schreiben. HandVero übernimmt dann
        regelmäßig die Termine Ihres Outlook-Kalenders von 30 Tagen zurück bis 12 Monate voraus (Betreff, Zeit, Ort und den Anfang der Beschreibung).
        <b>Diese Termine sehen alle Mitarbeiter Ihrer Firma in HandVero.</b> Verbinden Sie deshalb nur einen Kalender, dessen Termine Ihr Team sehen darf. Wir
        speichern die Zugriffsschlüssel verschlüsselt. Sie können die Verbindung jederzeit in HandVero oder in Ihrem Microsoft-Konto trennen.
        Rechtsgrundlage ist Art. 6 Abs. 1 Buchstabe b DSGVO (die von Ihnen gewünschte Funktion).
      </p>

      <H>11. Sicherheit</H>
      <p>
        Die Übertragung ist per TLS verschlüsselt. Passwörter speichern wir nur als Hash. Die Daten jeder Firma sind getrennt, Mitarbeiter sehen nur, was
        ihnen freigegeben ist. Trotzdem gibt es keine absolute Sicherheit. Beachten Sie auch den Hinweis zur Datensicherung in unseren
        Nutzungsbedingungen.
      </p>

      <H>12. Automatisierte Entscheidungen</H>
      <p>
        Ausschließlich die Prüfung der Registrierung (Punkt 6) erfolgt automatisch. Ein Profiling zu Werbezwecken gibt es nicht. Ihre Rechte dabei stehen in Punkt 6.
      </p>

      <H>13. Speicherdauer</H>
      <ul className="checks">
        <li>Konto- und Firmendaten: bis Sie das Konto beziehungsweise die Firma löschen. Das geht in HandVero unter „Mein Konto“.</li>
        <li>Ihre Inhalte (Fotos, Dokumente und so weiter): bis Sie sie löschen oder die Firma löschen. Mit dem Löschen der Firma werden sie gelöscht.</li>
        <li>Anmeldungen (Geräteliste): bis 30 Tage, die Einträge löschen wir spätestens 7 Tage nach Ablauf.</li>
        <li>Server-Protokolle: einige Tage bis wenige Wochen, siehe Punkt 3.</li>
        <li>Nutzungsstatistik: 12 Monate.</li>
        <li>Prüfung der Registrierung: 90 Tage. Nicht bestätigte Registrierungen: 30 Tage.</li>
      </ul>
      <p>
        Gesetzliche Aufbewahrungspflichten (etwa nach Handels- und Steuerrecht) bleiben unberührt. Sie gelten für Ihre eigenen Unterlagen und liegen
        in Ihrer Verantwortung.
      </p>

      <H>14. Ihre Rechte</H>
      <p>Sie haben gegenüber uns das Recht auf:</p>
      <ul className="checks">
        <li>Auskunft über Ihre gespeicherten Daten (Art. 15 DSGVO)</li>
        <li>Berichtigung falscher Daten (Art. 16)</li>
        <li>Löschung (Art. 17)</li>
        <li>Einschränkung der Verarbeitung (Art. 18)</li>
        <li>Herausgabe Ihrer Daten in gängigem Format (Art. 20). Jede Datei können Sie einzeln herunterladen. Eine vollständige Ausgabe aller Daten Ihrer Firma schicken wir Ihnen auf Anfrage innerhalb von 14 Tagen.</li>
        <li>Widerruf einer Einwilligung mit Wirkung für die Zukunft (Art. 7 Abs. 3)</li>
      </ul>
      <div className="notice info">
        <b>Widerspruchsrecht:</b> Verarbeiten wir Daten aufgrund unseres berechtigten Interesses (Art. 6 Abs. 1 Buchstabe f DSGVO), können Sie aus Gründen, die sich aus Ihrer besonderen Situation
        ergeben, jederzeit widersprechen. Wir verarbeiten die Daten dann nicht mehr, es sei denn, wir können zwingende schutzwürdige Gründe nachweisen oder die Verarbeitung dient der
        Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen.
      </div>
      <p>
        Schreiben Sie uns einfach an <a href={`mailto:${PROVIDER.email}`}>{PROVIDER.email}</a>. Betrifft Ihre Anfrage Daten, für die ein HandVero-Kunde
        verantwortlich ist (zum Beispiel Fotos eines Auftrags), leiten wir sie an diesen weiter.
      </p>
      <p>
        Sie können sich außerdem bei einer Datenschutz-Aufsichtsbehörde beschweren. Für uns zuständig ist die {DATA_AUTHORITY.name}, {DATA_AUTHORITY.street},{" "}
        {DATA_AUTHORITY.city}, {DATA_AUTHORITY.web}.
      </p>

      <H>15. Pflicht zur Bereitstellung</H>
      <p>
        Ohne die Angaben bei der Registrierung können wir kein Konto anlegen. Weitere Angaben sind freiwillig. Gegenüber Ihren Kunden und
        Mitarbeitern entscheiden Sie selbst, welche Daten Sie in HandVero ablegen.
      </p>

      <H>16. Änderungen</H>
      <p>Wir passen diese Erklärung an, wenn sich HandVero oder die Rechtslage ändert. Es gilt die jeweils hier veröffentlichte Fassung. Stand: {LEGAL_UPDATED}.</p>
    </Part>
  );
}

function Agb() {
  return (
    <Part id="agb" title="Nutzungsbedingungen (AGB)">
      <p className="lead">Diese Bedingungen gelten für die Nutzung von HandVero. Bitte lesen Sie sie, bevor Sie sich registrieren.</p>

      <H>1. Anbieter und Geltungsbereich</H>
      <p>
        Anbieter von HandVero ist {PROVIDER.owner}, {ADDRESS} (im Folgenden „wir“). Diese Bedingungen gelten für alle Verträge über die Nutzung von
        HandVero zwischen uns und Ihnen als Kunde.
      </p>
      <p>
        HandVero richtet sich <b>ausschließlich an Unternehmer</b> im Sinne von § 14 BGB, also an Betriebe, Selbstständige und Freiberufler, die HandVero
        für ihre gewerbliche oder berufliche Tätigkeit nutzen. Mit der Registrierung bestätigen Sie, dass Sie als Unternehmer handeln. Privatpersonen
        nutzen das Angebot „HandVero Zuhause“ mit eigenen <Link to="/privat/agb">Bedingungen</Link>. Für die Firmenanmeldung besteht deshalb kein gesetzliches Widerrufsrecht für Verbraucher. Abweichende Bedingungen des Kunden gelten
        nicht, auch wenn wir ihnen nicht ausdrücklich widersprechen.
      </p>

      <H>2. Leistung und Startphase</H>
      <p>
        HandVero ist eine Software im Internet, die Sie über den Browser und mit der Web-App auf dem Handy nutzen. Sie hilft, Aufträge, Fotos, Dokumente,
        Nachweise, Termine und Absprachen zu ordnen.
      </p>
      <p>
        <b>HandVero befindet sich in der Startphase.</b> Der Funktionsumfang ist noch nicht vollständig. Die Windows-Software steht im Kundenbereich zum Herunterladen bereit. Weitere Funktionen (zum Beispiel Protokolle,
        Online-Schulungen und das Zeichenprogramm) sind noch nicht freigegeben. Soweit sie schon im Browser am PC erreichbar sind, handelt es sich um Vorabversionen, die Fehler enthalten und sich
        ändern können. Einen Anspruch darauf, dass eine bestimmte Funktion zu einem bestimmten Zeitpunkt zur Verfügung steht, haben Sie nicht. Welche Funktionen heute nutzbar sind, steht auf unserer
        Website.
      </p>
      <p>
        Wir dürfen Funktionen ändern oder einstellen, wenn dafür ein triftiger Grund besteht (zum Beispiel Sicherheit, technische Entwicklung oder eine geänderte Rechtslage) und die Änderung für Sie
        zumutbar ist. Fällt eine Funktion weg, die Sie nutzen, sagen wir Ihnen das mindestens vier Wochen vorher, damit Sie Ihre Daten sichern können.
      </p>

      <H>3. Vertragsschluss, Registrierung und Zugang</H>
      <p>
        Mit dem Absenden der Registrierung geben Sie ein Angebot ab. Der Vertrag kommt zustande, wenn Sie Ihre E-Mail-Adresse bestätigen. Vertragssprache ist Deutsch. Diese Bedingungen können Sie hier jederzeit
        abrufen und über die Druckfunktion Ihres Browsers speichern. Eingabefehler können Sie vor dem Absenden im Formular korrigieren. Wir speichern den Zeitpunkt Ihrer Zustimmung und die Fassung dieser Bedingungen.
      </p>
      <ul className="checks">
        <li>Sie müssen bei der Registrierung richtige und vollständige Angaben machen und Ihre E-Mail-Adresse bestätigen.</li>
        <li>Wir prüfen Registrierungen automatisch und können sie ablehnen, wenn der Verdacht auf Missbrauch besteht. Wir nennen den Grund.</li>
        <li>Halten Sie Ihre Zugangsdaten geheim. Geben Sie sie nicht weiter, und melden Sie uns Missbrauch unverzüglich.</li>
        <li>
          Der Inhaber des Firmenkontos legt Mitarbeiterkonten an und verwaltet sie. Sie sind für das Handeln Ihrer Mitarbeiter in HandVero verantwortlich.
        </li>
      </ul>

      <H>4. Kosten</H>
      <p>
        Die ersten {FOUNDER_SLOTS} Firmen, die ihre Registrierung bestätigen, nutzen HandVero mindestens 3 Jahre kostenlos mit bis zu 3 Mitarbeitern
        (Startaktion). Während dieser 3 Jahre, gerechnet ab Bestätigung der Registrierung, kündigen wir den Vertrag nur aus wichtigem Grund (Punkt 11). Die Windows-Software steht allen Firmen im Kundenbereich zur Verfügung, auch mit der kostenlosen Standardlizenz. Zusatzfunktionen, die später dazukommen, stehen den Startplatz-Firmen und später den Jahresabo-Kunden zu wie bezahlenden Kunden, soweit nicht anders angegeben. Jede weitere Firma nutzt HandVero kostenlos mit 1 Mitarbeiter. Nach Ende der Startaktion können Sie HandVero kostenlos mit 1
        Mitarbeiter weiter nutzen.
      </p>
      <p>
        Für mehr Mitarbeiter gibt es Abos: Solo (1 Mitarbeiter, {formatEuro(SUBSCRIPTION_TIERS[0]!.monthlyCents)} im Monat), Team (bis 3 Mitarbeiter, {formatEuro(SUBSCRIPTION_TIERS[1]!.monthlyCents)} im Monat) und Betrieb (4 bis 10 Mitarbeiter, {formatEuro(SUBSCRIPTION_TIERS[2]!.monthlyCents)} im Monat plus {formatEuro(SUBSCRIPTION_TIERS[2]!.extraSeatMonthlyCents)} je Mitarbeiter ab dem vierten), jeweils pro Firma und bei Jahreszahlung mit zwei Monaten Nachlass, zuzüglich Umsatzsteuer, soweit
        anfallend. Ein Abo kommt nur zustande, wenn Sie es im Kundenbereich ausdrücklich buchen und die Zahlung bei Mollie bestätigen. Bezahlt wird über den Zahlungsdienst Mollie (Mollie B.V., Amsterdam). Das Abo verlängert sich nach Ablauf jedes bezahlten Zeitraums (Monat oder Jahr) um denselben Zeitraum und wird automatisch abgebucht.
        Sie können es jederzeit im Kundenbereich kündigen; dann wird nichts mehr abgebucht und Sie nutzen HandVero bis zum Ende des bezahlten Zeitraums. Für jede Zahlung erhalten Sie automatisch eine Rechnung per E-Mail. Ohne Ihre ausdrückliche Zustimmung entstehen Ihnen keine Kosten. Das gilt auch für spätere Zusatzfunktionen (zum Beispiel das Zeichenprogramm): Sie kosten nur, wenn Sie sie ausdrücklich buchen.
      </p>

      <H>5. Verfügbarkeit</H>
      <p>
        Wir bemühen uns um einen störungsfreien Betrieb, schulden aber in der Startphase keine bestimmte Verfügbarkeit. Wartungen, Aktualisierungen und
        Störungen können zu Unterbrechungen führen. Wir kündigen planbare Wartungen nach Möglichkeit an.
      </p>

      <H>6. Ihre Daten und Datensicherung</H>
      <p>
        Die Inhalte, die Sie in HandVero ablegen, gehören Ihnen. Sie räumen uns die Rechte ein, die wir brauchen, um sie für Sie zu speichern, anzuzeigen
        und zu übertragen.
      </p>
      <p>
        <b>Wichtig: In der Startphase sind Sie selbst für die Sicherung Ihrer Daten verantwortlich.</b> Wir sichern die Server nach bestem Wissen, sagen
        aber keine regelmäßige Datensicherung und keine Wiederherstellung zu. Speichern Sie wichtige Dokumente, Fotos und Nachweise deshalb zusätzlich
        selbst, zum Beispiel auf Ihrem PC oder in Ihrem Firmenarchiv. HandVero ersetzt kein Archiv. Gesetzliche Aufbewahrungs- und Nachweispflichten
        bleiben bei Ihnen.
      </p>

      <H>7. Pflichten bei der Nutzung</H>
      <ul className="checks">
        <li>Sie dürfen nur Inhalte ablegen, an denen Sie die Rechte haben und die nicht gegen Gesetze verstoßen.</li>
        <li>
          Wenn Sie personenbezogene Daten Ihrer Kunden oder Mitarbeiter ablegen, müssen Sie dafür eine Rechtsgrundlage haben und diese Personen
          informieren.
        </li>
        <li>Sie dürfen HandVero nicht stören, überlasten, angreifen oder Schutzmaßnahmen umgehen und keine Schadsoftware hochladen.</li>
        <li>Sie dürfen Ihren Zugang nicht an Dritte außerhalb Ihres Betriebs weitergeben.</li>
      </ul>
      <p>
        <b>Vorlagen sind keine Rechtsberatung.</b> Protokoll-, Unterweisungs- und Belehrungsvorlagen sind Muster. Sie müssen im Einzelfall selbst prüfen,
        ob sie für Ihren Zweck und nach den für Sie geltenden Vorschriften, Normen und Vorgaben ausreichen. Eine Unterschrift am Bildschirm ersetzt nicht
        in jedem Fall die gesetzliche Schriftform.
      </p>
      <p>
        <b>Wir stellen die Plattform, Sie verantworten Ihre Inhalte.</b> HandVero ist ein Werkzeug zum Ordnen und Erstellen. Fotos, Dokumente, Protokolle,
        Unterweisungen und Belehrungen, Angebote, Aufträge und Rechnungen erstellen und verantworten Sie. Das gilt für deren Richtigkeit und
        Vollständigkeit, für Pflichtangaben und Steuersätze (zum Beispiel Umsatzsteuer, Kleinunternehmerregelung, Steuerschuldnerschaft bei
        Bauleistungen, elektronische Rechnung) und dafür, dass sie für den jeweiligen Zweck und nach den für Sie geltenden Vorschriften genügen. Wir sind
        nicht Vertragspartner Ihrer Kunden und erbringen keine Rechts-, Steuer- oder Arbeitsschutzberatung. Pflichten, die das Gesetz Ihnen als
        Unternehmer auferlegt (zum Beispiel Prüfungen, Unterweisungen, Dokumentation und Aufbewahrung), bleiben bei Ihnen. Wir empfehlen, Vorlagen und
        Abläufe einmal durch Ihre Steuerberatung oder fachkundige Stelle prüfen zu lassen. Soweit ein Dritter uns wegen Inhalte in Anspruch nimmt, die Sie
        eingestellt haben, und Sie die Rechtsverletzung zu vertreten haben, stellen Sie uns im gesetzlich zulässigen Rahmen davon frei.
      </p>

      <H>8. Datenschutz und Auftragsverarbeitung</H>
      <p>
        Wie wir mit Daten umgehen, steht in der <a href="#datenschutz">Datenschutzerklärung</a>. Soweit wir für Sie personenbezogene Daten verarbeiten,
        gilt zusätzlich die <a href="#avv">Vereinbarung zur Auftragsverarbeitung</a>. Sie ist Teil dieses Vertrags.
      </p>

      <H>9. Nutzungsrechte an der Software</H>
      <p>
        Sie erhalten für die Dauer des Vertrags das einfache, nicht übertragbare Recht, HandVero für Ihren Betrieb zu nutzen. Alle Rechte an HandVero,
        an den Texten, Grafiken und Vorlagen bleiben bei uns. Vorlagen dürfen Sie für Ihren Betrieb verwenden, aber nicht weiterverkaufen.
      </p>

      <H>10. Haftung</H>
      <p>Wir haften unbeschränkt</p>
      <ul className="checks">
        <li>bei Vorsatz und grober Fahrlässigkeit,</li>
        <li>bei Verletzung von Leben, Körper oder Gesundheit,</li>
        <li>nach dem Produkthaftungsgesetz und in allen Fällen, in denen das Gesetz zwingend eine Haftung vorschreibt.</li>
      </ul>
      <p>
        Bei leicht fahrlässiger Verletzung einer wesentlichen Vertragspflicht (einer Pflicht, auf deren Einhaltung Sie vertrauen dürfen) haften wir nur
        für den vorhersehbaren, typischen Schaden. Im Übrigen haften wir bei leichter Fahrlässigkeit nicht. Solange Sie HandVero unentgeltlich nutzen,
        haften wir nur bei Vorsatz und grober Fahrlässigkeit, bei Übernahme einer Garantie und bei arglistigem Verschweigen eines Mangels sowie in den oben genannten zwingenden Fällen.
      </p>
      <p>
        Bei leichter Fahrlässigkeit haften wir für den Verlust von Daten nur in Höhe des Aufwands, der nötig wäre, um die Daten aus einer regelmäßigen eigenen
        Sicherung (Punkt 6) wiederherzustellen. Die unbeschränkte Haftung nach den Absätzen oben bleibt unberührt.
      </p>

      <H>11. Laufzeit und Beendigung</H>
      <p>
        Der Vertrag läuft auf unbestimmte Zeit. Sie können ihn jederzeit beenden, indem Sie uns das in Textform (zum Beispiel per E-Mail) mitteilen oder Ihr Konto oder Ihre Firma in HandVero unter „Mein Konto“ löschen. Wir
        können den Vertrag mit einer Frist von 30 Tagen in Textform kündigen. Das Recht beider Seiten zur fristlosen Kündigung aus wichtigem Grund
        bleibt unberührt. Ein wichtiger Grund ist für uns zum Beispiel Missbrauch von HandVero oder ein schwerer Verstoß gegen diese Bedingungen.
      </p>
      <p>
        Wenn wir kündigen, haben Sie 30 Tage Zeit, Ihre Daten zu sichern, außer ein wichtiger Grund steht dem entgegen. Danach löschen wir Ihre Daten.
      </p>

      <H>12. Änderungen dieser Bedingungen</H>
      <p>
        Wir dürfen diese Bedingungen für die Zukunft ändern, wenn das nötig ist, zum Beispiel wegen neuer Funktionen oder geänderter Gesetze. Wir
        teilen Änderungen mindestens vier Wochen vorher per E-Mail mit. Widersprechen Sie nicht innerhalb dieser Frist, gelten die neuen
        Bedingungen. Wir weisen in der Mitteilung auf diese Folge hin. Änderungen, die den Leistungsumfang oder den Preis wesentlich betreffen oder das Verhältnis von Leistung und Gegenleistung zu Ihren
        Lasten verschieben, werden nicht durch Schweigen wirksam. Dafür holen wir Ihre ausdrückliche Zustimmung ein. Widersprechen Sie, können wir den Vertrag zum Änderungstermin kündigen.
      </p>

      <H>13. Schlussbestimmungen</H>
      <p>
        Es gilt deutsches Recht unter Ausschluss des UN-Kaufrechts. Ist der Kunde Kaufmann, juristische Person des öffentlichen Rechts oder
        öffentlich-rechtliches Sondervermögen, ist Gerichtsstand der Sitz des Anbieters. Sollte eine Bestimmung unwirksam sein, bleiben die übrigen
        wirksam. Stand: {LEGAL_UPDATED}.
      </p>
    </Part>
  );
}

function Avv() {
  return (
    <Part id="avv" title="Vereinbarung zur Auftragsverarbeitung (Art. 28 DSGVO)">
      <p className="lead">
        Diese Vereinbarung gilt, sobald Sie sich registrieren, und ist Teil der Nutzungsbedingungen. Sie, der Kunde, sind „Verantwortlicher“, wir sind
        „Auftragsverarbeiter“.
      </p>

      <H>1. Gegenstand und Dauer</H>
      <p>
        Wir verarbeiten personenbezogene Daten in Ihrem Auftrag, um HandVero bereitzustellen. Die Vereinbarung läuft so lange, wie wir Daten für Sie
        verarbeiten.
      </p>

      <H>2. Art, Zweck, Daten und Betroffene</H>
      <ul className="checks">
        <li>
          <b>Art und Zweck:</b> Speichern, Anzeigen, Ordnen, Übertragen und Löschen der Inhalte, die Sie in HandVero ablegen, sowie Versand von Mails in
          Ihrem Auftrag.
        </li>
        <li>
          <b>Arten von Daten:</b> Kontaktdaten (Name, Adresse, E-Mail, Telefon), Auftrags- und Projektdaten, Fotos, Dokumente, Notizen, Chat-Nachrichten, Termine,
          Unterschriften, Protokoll- und Schulungsnachweise, Nutzungs- und Anmeldedaten.
        </li>
        <li>
          <b>Betroffene:</b> Ihre Mitarbeiter, Ihre Kunden und deren Ansprechpartner, Lieferanten und weitere Personen, deren Daten Sie ablegen.
        </li>
      </ul>

      <H>3. Weisungen</H>
      <p>
        Wir verarbeiten die Daten nur nach Ihren dokumentierten Weisungen. Ihre Weisungen sind die Bedienung von HandVero und Ihre Mitteilungen an uns.
        Halten wir eine Weisung für rechtswidrig, weisen wir Sie darauf hin. Daten übermitteln wir in ein Land außerhalb der EU nur auf Ihre Weisung oder wenn die Voraussetzungen der Art. 44 ff. DSGVO erfüllt
        sind. Sind wir gesetzlich zu einer Verarbeitung verpflichtet, teilen wir Ihnen das vorher mit, soweit das Gesetz es nicht verbietet.
      </p>

      <H>4. Vertraulichkeit</H>
      <p>Wir verpflichten alle Personen, die Zugriff auf die Daten haben, zur Vertraulichkeit.</p>

      <H>5. Sicherheitsmaßnahmen (Art. 32 DSGVO)</H>
      <ul className="checks">
        <li>Server in einem Rechenzentrum in Deutschland, verschlüsselte Übertragung (TLS).</li>
        <li>Passwörter nur als Hash, Anmeldung mit Bestätigung der E-Mail-Adresse, Sperre bei Missbrauch.</li>
        <li>Trennung der Firmendaten: Jede Abfrage ist auf die Firma des Nutzers begrenzt. Mitarbeiter sehen nur freigegebene Projekte.</li>
        <li>Zugriff durch uns auf Ihr Konto nur, wenn Sie ihn ausdrücklich und zeitlich begrenzt erlauben. Freigabe und Nutzung der Freigabe werden festgehalten.</li>
        <li>Zugriffsschlüssel für Dienste wie Outlook liegen verschlüsselt.</li>
        <li>Eine regelmäßige Datensicherung durch uns ist in der Startphase nicht zugesagt. Sie sichern wichtige Daten selbst (siehe Nutzungsbedingungen, Punkt 6).</li>
      </ul>

      <H>6. Unterauftragnehmer</H>
      <p>Sie stimmen dem Einsatz folgender Unterauftragnehmer zu:</p>
      <ul className="checks">
        <li>Hetzner Online GmbH, Gunzenhausen: Hosting der Server in Deutschland.</li>
        <li>Microsoft Ireland Operations Limited, Dublin: Mailversand über Microsoft 365 und, nur auf Ihre Einrichtung, Outlook-Kalender. Microsoft ist nach dem EU-US Data Privacy Framework zertifiziert, zusätzlich gelten die Standardvertragsklauseln der EU.</li>
        <li>Mollie B.V., Amsterdam, Niederlande: Zahlungsabwicklung für kostenpflichtige Abos von HandVero. Betrifft nicht die Inhalte, die Sie in HandVero speichern.</li>
      </ul>
      <p>
        Über beabsichtigte Änderungen informieren wir Sie vorab per E-Mail. Sie können innerhalb von vier Wochen widersprechen. Wir schließen mit allen
        Unterauftragnehmern Verträge, die dieser Vereinbarung entsprechen.
      </p>

      <H>7. Unterstützung</H>
      <p>
        Wir unterstützen Sie mit geeigneten Maßnahmen, damit Sie Anfragen betroffener Personen beantworten können (Auskunft, Berichtigung, Löschung,
        Herausgabe). Meldet sich jemand direkt bei uns, leiten wir die Anfrage an Sie weiter. Wir helfen Ihnen außerdem bei Datenschutz-Folgenabschätzungen
        und Meldungen von Datenpannen sowie bei einer vorherigen Konsultation der Aufsichtsbehörde, soweit das in unserem Bereich liegt.
      </p>

      <H>8. Datenpannen</H>
      <p>Wir melden Ihnen eine Verletzung des Schutzes personenbezogener Daten unverzüglich, nachdem sie uns bekannt wurde, mit den uns bekannten Einzelheiten.</p>

      <H>9. Löschung und Rückgabe</H>
      <p>
        Sie können jede Datei einzeln herunterladen und Ihre Daten selbst löschen. Eine vollständige Ausgabe aller Daten Ihrer Firma schicken wir Ihnen auf Anfrage innerhalb von 14 Tagen. Nach Ende des Vertrags löschen wir Ihre Daten, nachdem Sie ausreichend Zeit
        zur Sicherung hatten (siehe Nutzungsbedingungen, Punkt 11), soweit uns das Gesetz nicht zur Aufbewahrung verpflichtet.
      </p>

      <H>10. Nachweise und Kontrolle</H>
      <p>
        Wir stellen Ihnen auf Anfrage die Informationen zur Verfügung, die Sie brauchen, um unsere Einhaltung dieser Vereinbarung zu prüfen. Prüfungen
        vor Ort sind nach vorheriger Abstimmung während der üblichen Geschäftszeiten möglich, soweit sie den Betrieb nicht unverhältnismäßig stören.
      </p>

      <H>11. Haftung</H>
      <p>Es gilt Art. 82 DSGVO. Im Übrigen gilt die Haftungsregelung der Nutzungsbedingungen, soweit das Gesetz nichts anderes zwingend vorgibt.</p>
    </Part>
  );
}

export function Rechtliches() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);
  return (
    <Page name="rechtliches">
      <div className="wrap stack-lg" style={{ maxWidth: 760 }}>
        <PageHead
          eyebrow="Impressum, Datenschutz, AGB"
          title="Rechtliches"
          lead={`Stand: ${LEGAL_UPDATED}. Hier finden Sie alle rechtlichen Angaben zu HandVero.`}
        />
        <nav className="legal-nav" aria-label="Rechtliches">
          <a href="#impressum">Impressum</a>
          <a href="#datenschutz">Datenschutz</a>
          <a href="#agb">Nutzungsbedingungen</a>
          <a href="#avv">Auftragsverarbeitung</a>
        </nav>
        <Impressum />
        <Datenschutz />
        <Agb />
        <Avv />
        <p className="muted small">
          Fragen zu diesen Texten? Schreiben Sie uns an <a href={`mailto:${PROVIDER.email}`}>{PROVIDER.email}</a> oder <Link to="/kontakt">besuchen Sie die Kontaktseite</Link>.
        </p>
      </div>
    </Page>
  );
}
